import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import SelectGroup from '../../inputs/select/grouped/select-group';
import InputMoney from '../../inputs/money/input-money';
import Input from '../../inputs/common/input';

interface GroupedOption {
  label: string;
  value: any;
  name?: string;
  id?: any;
  group?: string;
}

interface Rubrica {
  id: string;
  name: string;
  description: string;
  value: string;
  label: string;
  group?: string;
}

interface RubricaFieldProps {
  index: number;
  availableRubricas: Rubrica[];
  onRemove: () => void;
  isLoading: boolean;
  isLoadingRubricas: boolean;
  canRemove: boolean;
  getUsedRubricaIds: () => string[];
  fieldId: string;
  canCreateRubrica?: boolean;
  userCompanyId?: string;
  onRubricaCreated?: (newRubrica: Rubrica) => void;
}

export default function RubricaField({
  index,
  availableRubricas,
  onRemove,
  isLoading,
  isLoadingRubricas,
  canRemove,
  getUsedRubricaIds,
  fieldId,
  canCreateRubrica = false,
  userCompanyId,
  onRubricaCreated
}: RubricaFieldProps) {
  const { control, setValue, watch } = useFormContext();
  
  // ✅ Usar watch em vez de estado local para evitar dessincronização
  const currentRubricaId = watch(`rubricas.${index}.rubricaId`) || '';
  
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [newRubricaName, setNewRubricaName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const isUpdatingRef = useRef(false);

  // ✅ Memoizar opções disponíveis para evitar recálculos desnecessários
  const availableOptions = useMemo((): GroupedOption[] => {
    const usedIds = getUsedRubricaIds();
    
    const filtered = availableRubricas.filter(rubrica => {
      return rubrica.id === currentRubricaId || !usedIds.includes(rubrica.id);
    });

    return filtered.map(rubrica => ({
      label: rubrica.label,
      value: rubrica.value,
      name: rubrica.name,
      id: rubrica.id,
      group: rubrica.group || 'Sem grupo'
    }));
  }, [availableRubricas, currentRubricaId, getUsedRubricaIds]);

  const handleCreateRubrica = async () => {
    if (!newRubricaName.trim()) {
      setCreateError('Nome é obrigatório');
      return;
    }

    if (newRubricaName.trim().length < 3) {
      setCreateError('Nome deve ter no mínimo 3 caracteres');
      return;
    }

    if (!userCompanyId) {
      setCreateError('ID da empresa não disponível');
      return;
    }

    setIsCreating(true);
    setCreateError('');

    try {
      const { createRubrica } = await import('../../../features/rubrica/service/rubrica');
      
      const newRubrica = await createRubrica({
        name: newRubricaName.trim(),
        description: newRubricaName.trim(),
        clientId: userCompanyId
      });

      const formattedRubrica: Rubrica = {
        id: newRubrica.id,
        name: newRubrica.name,
        description: newRubrica.description || newRubrica.name,
        value: newRubrica.id,
        label: newRubrica.name,
        group: 'Minhas Rubricas'
      };

      if (onRubricaCreated) {
        onRubricaCreated(formattedRubrica);
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      isUpdatingRef.current = true;
      setValue(`rubricas.${index}.rubricaId`, formattedRubrica.id, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });

      setNewRubricaName('');
      setIsCreateMode(false);

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    } catch (error: any) {
      setCreateError(error?.response?.data?.message || 'Erro ao criar rubrica');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRubricaChange = useCallback((newValue: string | null) => {
    isUpdatingRef.current = true;
    const valueToSet = newValue || '';
    
    setValue(`rubricas.${index}.rubricaId`, valueToSet, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
    
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 100);
  }, [index, setValue]);

  const handleModeChange = (createMode: boolean) => {
    setIsCreateMode(createMode);
    setCreateError('');
    setNewRubricaName('');
    
    if (!createMode && !currentRubricaId) {
      setValue(`rubricas.${index}.rubricaId`, '', {
        shouldValidate: true
      });
    }
  };

  // ✅ Removido o useEffect problemático que causava loops

  return (
    <div className="rubrica-field" key={`rubrica-field-${fieldId}`}>
      <div className="rubrica-field__row" style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: '12px',
        width: '100%'
      }}>
        <div style={{ 
          flex: '1 1 auto',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {canCreateRubrica && (
            <div style={{ 
              marginBottom: '8px',
              height: '24px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <label style={{ 
                fontSize: '13px', 
                fontWeight: 500,
                color: '#495057',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                margin: 0
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  gap: '6px',
                  margin: 0
                }}>
                  <input
                    type="radio"
                    name={`mode-${fieldId}`}
                    checked={!isCreateMode}
                    onChange={() => handleModeChange(false)}
                    disabled={isLoading || isLoadingRubricas || isCreating}
                    style={{ cursor: 'pointer', margin: 0 }}
                  />
                  <span>Selecionar existente</span>
                </label>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer',
                  gap: '6px',
                  margin: 0
                }}>
                  <input
                    type="radio"
                    name={`mode-${fieldId}`}
                    checked={isCreateMode}
                    onChange={() => handleModeChange(true)}
                    disabled={isLoading || isLoadingRubricas || isCreating}
                    style={{ cursor: 'pointer', margin: 0 }}
                  />
                  <span>Criar nova</span>
                </label>
              </label>
            </div>
          )}

          <div style={{ height: '55px', display: 'flex', alignItems: 'stretch' }}>
            {!isCreateMode ? (
              <Controller
                name={`rubricas.${index}.rubricaId`}
                control={control}
                rules={{ required: 'Selecione uma rubrica' }}
                render={({ field, fieldState: { error } }) => (
                  <SelectGroup
                    key={`select-${fieldId}-${availableOptions.length}`}
                    data={availableOptions}
                    onChange={handleRubricaChange}
                    value={field.value || ''}
                    placeholder="Selecione uma rubrica"
                    error={error?.message}
                    disabled={isLoading || isLoadingRubricas}
                    height={55}
                    width="100%"
                  />
                )}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                width: '100%',
                height: '55px'
              }}>
                <div style={{ flex: 1 }}>
                  <Input
                    value={newRubricaName}
                    onChange={(e: any) => {
                      const newValue = e.target.value;
                      setNewRubricaName(newValue);
                      setCreateError('');
                    }}
                    placeholder="Digite o nome da nova rubrica"
                    error={createError}
                    disabled={isLoading || isLoadingRubricas || isCreating}
                    width="100%"
                    height={55}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleCreateRubrica}
                  disabled={isLoading || isLoadingRubricas || isCreating || !newRubricaName.trim()}
                  style={{
                    height: '55px',
                    padding: '0 20px',
                    backgroundColor: isCreating ? '#6c757d' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: isCreating ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s',
                    opacity: (!newRubricaName.trim() || isCreating) ? 0.6 : 1,
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    if (!isCreating && newRubricaName.trim()) {
                      e.currentTarget.style.backgroundColor = '#5568d3';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCreating) {
                      e.currentTarget.style.backgroundColor = '#667eea';
                    }
                  }}
                >
                  {isCreating ? 'Criando...' : 'Criar'}
                </button>
              </div>
            )}
          </div>

          {isCreateMode && newRubricaName.trim() && newRubricaName.trim().length < 3 && !createError && (
            <div style={{
              fontSize: '12px',
              color: '#6c757d',
              marginTop: '4px',
              marginLeft: '4px',
              height: '16px'
            }}>
              Mínimo 3 caracteres
            </div>
          )}
        </div>

        <div style={{ 
          width: '200px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {canCreateRubrica && (
            <div style={{ 
              marginBottom: '8px',
              height: '24px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <label style={{ 
                fontSize: '13px', 
                fontWeight: 500,
                color: '#495057',
                margin: 0
              }}>
                Valor
              </label>
            </div>
          )}

          <div style={{ height: '55px' }}>
            <Controller
              name={`rubricas.${index}.valor`}
              control={control}
              rules={{
                required: 'Informe o valor',
                min: { value: 0.01, message: 'Valor deve ser maior que zero' },
                validate: (value) => {
                  const numValue = Number(value);
                  if (isNaN(numValue) || numValue <= 0) {
                    return 'Valor deve ser maior que zero';
                  }
                  return true;
                }
              }}
              render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                <InputMoney
                  key={`money-${fieldId}`}
                  name={name}
                  value={value}
                  onChange={(e) => {
                    const numericValue = Number(e.target.value);
                    onChange(numericValue);
                  }}
                  placeholder="Valor"
                  error={error?.message}
                  disabled={isLoading || isLoadingRubricas}
                  width="100%"
                  height={55}
                  prefix="R$ "
                />
              )}
            />
          </div>
        </div>

        <div style={{ 
          width: '40px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          {canCreateRubrica && (
            <div style={{ 
              marginBottom: '8px',
              height: '24px'
            }} />
          )}

          <div style={{ height: '55px', display: 'flex', alignItems: 'center' }}>
            <button
              type="button"
              onClick={onRemove}
              className="rubrica-field__remove"
              disabled={!canRemove || isLoading}
              title="Remover rubrica"
              style={{
                width: '40px',
                height: '40px',
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: canRemove && !isLoading ? 'pointer' : 'not-allowed',
                fontSize: '20px',
                opacity: canRemove && !isLoading ? 1 : 0.5,
                transition: 'opacity 0.2s'
              }}
            >
              🗑️
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}